// Render PDF pages to images with pdf.js (lazy-loaded: only users who open a PDF pay for it) and
// detect measures on the same pixels, so boxes line up exactly with what is shown.
import { detectMeasures, type Box } from "../core/pdf/detectMeasures";

export interface RenderedPage { url: string; aspect: number; measures: Box[] }

const RENDER_WIDTH = 1400;   // px: sharp on 3× phones, enough resolution for 1-px staff lines

async function pdfjs() {
  // legacy build: wider Safari support than the modern build
  const lib = await import("pdfjs-dist/legacy/build/pdf.mjs");
  const worker = await import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url");
  lib.GlobalWorkerOptions.workerSrc = worker.default;
  return lib;
}

/**
 * Facts engraved as text, read from the PDF text layer (exact, no OCR):
 *  - a multi-measure rest is drawn once but lasts N bars: N is the number above the H-bar
 *    (no number found → the box counts as one bar)
 *  - a volta bracket starts with "1." / "2." just above the staff at its first bar
 *  - chord symbols above the staff (used to line the sheet up with the analysed chords)
 */
const CHORD = /^[A-G][#b♯♭]?(?:m(?!aj)|maj|min|dim|aug|sus|add|M|\+|°|ø)?[0-9]*(?:sus[24]?|add[0-9]+|[#b♯♭][0-9]+)*(?:\/[A-G][#b♯♭]?)?$/;
async function annotateFromText(lib: Awaited<ReturnType<typeof pdfjs>>, page: any, viewport: any, W: number, H: number, boxes: Box[]) {
  const text = await page.getTextContent();
  // engravers often emit one glyph per text item ("E" "♭", "D" "/" "F" "♯", "1" "."): join items on
  // the same baseline that touch into words first, then split words on whitespace
  const items = (text.items as any[])
    .filter((it) => it.str)
    .map((it) => {
      const tx = lib.Util.transform(viewport.transform, it.transform);
      return { str: it.str as string, x: tx[4], r: tx[4] + it.width * viewport.scale, y: tx[5] };
    })
    .sort((p, q) => (Math.abs(p.y - q.y) > H * 0.003 ? p.y - q.y : p.x - q.x));
  const words: { str: string; x: number; r: number; y: number }[] = [];
  for (const it of items) {
    const last = words[words.length - 1];
    if (last && Math.abs(it.y - last.y) <= H * 0.003 && it.x - last.r <= W * 0.004 && it.x >= last.x) {
      last.str += it.str; last.r = Math.max(last.r, it.r);
    } else words.push({ ...it });
  }
  const nums: { x: number; xl: number; y: number; n: number }[] = [];
  const voltas: { xl: number; y: number; n: number }[] = [];
  const chords: { x: number; y: number; sym: string }[] = [];
  for (const wd of words) {
    // a word can still hold several tokens ("G E♭"): place each by its character offset
    for (const m of wd.str.matchAll(/\S+/g)) {
      const tok = m[0];
      const xl = (wd.x + (m.index! / Math.max(1, wd.str.length)) * (wd.r - wd.x)) / W;
      const xr = (wd.x + ((m.index! + tok.length) / Math.max(1, wd.str.length)) * (wd.r - wd.x)) / W;
      const y = wd.y / H;
      if (/^\d{1,3}$/.test(tok)) nums.push({ x: (xl + xr) / 2, xl, y, n: +tok });
      const v = /^([1-9])(\.|st|nd|rd|th)$/.exec(tok);
      if (v) voltas.push({ xl, y, n: +v[1] });
      if (CHORD.test(tok)) chords.push({ x: xl, y, sym: tok });
    }
  }
  for (const b of boxes) {
    const above = (y: number, reach: number) => y > b.y0 - reach && y <= b.staffTop + 0.005;
    if (b.multiRest) {
      const mid = (b.x0 + b.x1) / 2;
      const hit = nums
        .filter((t) => t.x > b.x0 && t.x < b.x1 && above(t.y, 0.02) && t.n >= 2)
        .sort((p, q) => Math.abs(p.x - mid) - Math.abs(q.x - mid))[0];
      if (hit) b.span = hit.n;
    }
    const v = voltas.find((t) => t.xl >= b.x0 - 0.01 && t.xl <= b.x0 + (b.x1 - b.x0) * 0.35 && above(t.y, 0.05));
    if (v) b.ending = v.n;
    const cs = chords.filter((c) => c.x >= b.x0 && c.x < b.x1 && above(c.y, 0.05)).sort((p, q) => p.x - q.x);
    if (cs.length) b.chords = cs.map((c) => c.sym);
  }
}

/** `detect` = false reuses stored boxes (re-opening a song) and only rebuilds the images */
export async function renderPdf(data: ArrayBuffer, detect: boolean): Promise<RenderedPage[]> {
  const lib = await pdfjs();
  const doc = await lib.getDocument({ data: data.slice(0) }).promise;   // pdf.js detaches the buffer
  const out: RenderedPage[] = [];
  try {
    for (let n = 1; n <= doc.numPages; n++) {
      const page = await doc.getPage(n);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: RENDER_WIDTH / base.width });
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const ctx = canvas.getContext("2d", { willReadFrequently: detect })!;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // intent "print": render in one go without requestAnimationFrame — rAF stalls in background tabs
      // and we draw off-screen into an image anyway
      await page.render({ canvasContext: ctx, viewport, intent: "print" }).promise;
      const measures = detect ? detectMeasures(ctx.getImageData(0, 0, canvas.width, canvas.height)).measures : [];
      if (measures.length) await annotateFromText(lib, page, viewport, canvas.width, canvas.height, measures);
      const blob: Blob = await new Promise((res) => canvas.toBlob((b) => res(b!), "image/png"));
      out.push({ url: URL.createObjectURL(blob), aspect: canvas.height / canvas.width, measures });
      page.cleanup();
    }
  } finally {
    await doc.destroy();
  }
  return out;
}
